import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, Linkedin, MessageCircle, ArrowUp, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { profileData } from '../data/mock';
import Chatbot from './Chatbot';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

export const AnimateIn = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Expertise', path: '/expertise' },
  { name: 'Resources', path: '/resources' },
  { name: 'Payroll', path: '/payroll' },
  { name: 'Contact', path: '/contact' },
  { name: 'Admin', path: '/admin' },
];

const Layout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  return (
    <div className="min-h-screen flex flex-col font-body">
      <ScrollToTop />

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white/80 backdrop-blur-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-full bg-[#0B1F3A] flex items-center justify-center group-hover:bg-[#C9A646] transition-colors duration-300">
                <span className="text-[#C9A646] text-sm font-heading font-bold group-hover:text-[#0B1F3A] transition-colors duration-300">VS</span>
              </div>
              <div>
                <span className="text-[#0B1F3A] font-heading font-bold text-lg leading-tight block">Vivekanandan S</span>
                <span className="text-[#C9A646] text-[11px] font-body tracking-[0.15em] uppercase hidden sm:block">Labour Law Expert</span>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-body text-sm tracking-wide transition-colors duration-200 ${
                    location.pathname === link.path
                      ? 'text-[#C9A646] font-semibold'
                      : 'text-[#0B1F3A]/70 hover:text-[#C9A646]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/consultation">
                <Button className="bg-[#C9A646] hover:bg-[#B8953B] text-[#0B1F3A] font-semibold font-body rounded-sm px-6 h-10 text-sm">
                  Book Consultation
                </Button>
              </Link>
            </nav>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden text-[#0B1F3A] p-2"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-6 py-6 space-y-4">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block font-body text-base py-2 ${
                    location.pathname === link.path ? 'text-[#C9A646] font-semibold' : 'text-[#0B1F3A]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/consultation" className="block pt-2">
                <Button className="bg-[#C9A646] hover:bg-[#B8953B] text-[#0B1F3A] font-semibold font-body rounded-sm w-full h-12">
                  Book Consultation
                </Button>
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-[#0B1F3A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#C9A646] flex items-center justify-center">
                  <span className="text-[#0B1F3A] text-sm font-heading font-bold">VS</span>
                </div>
                <span className="font-heading font-bold text-lg">Vivekanandan S</span>
              </div>
              <p className="text-gray-400 font-body text-sm leading-relaxed">
                Labour Law Expert & PAN India Compliance Consultant with 30+ years of expertise.
              </p>
            </div>

            <div>
              <h4 className="font-heading font-bold text-[#C9A646] mb-4 text-sm tracking-wider uppercase">Quick Links</h4>
              <ul className="space-y-2">
                {navLinks.map(link => (
                  <li key={link.path}>
                    <Link to={link.path} className="text-gray-400 hover:text-white font-body text-sm transition-colors flex items-center gap-2">
                      <ChevronRight size={14} className="text-[#C9A646]/50" />
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-heading font-bold text-[#C9A646] mb-4 text-sm tracking-wider uppercase">Services</h4>
              <ul className="space-y-2">
                {['Labour Law Advisory', 'Compliance Audits', 'Statutory Compliance', 'Training Programs'].map(s => (
                  <li key={s}>
                    <Link to="/services" className="text-gray-400 hover:text-white font-body text-sm transition-colors flex items-center gap-2">
                      <ChevronRight size={14} className="text-[#C9A646]/50" />
                      {s}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-heading font-bold text-[#C9A646] mb-4 text-sm tracking-wider uppercase">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Mail size={16} className="text-[#C9A646] mt-0.5 shrink-0" />
                  <a href={`mailto:${profileData.email}`} className="text-gray-400 hover:text-white font-body text-sm transition-colors">
                    {profileData.email}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Phone size={16} className="text-[#C9A646] mt-0.5 shrink-0" />
                  <a href={`tel:${profileData.phone}`} className="text-gray-400 hover:text-white font-body text-sm transition-colors">
                    {profileData.phone}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={16} className="text-[#C9A646] mt-0.5 shrink-0" />
                  <span className="text-gray-400 font-body text-sm">{profileData.location}</span>
                </li>
              </ul>
              <div className="flex items-center gap-3 mt-6">
                <a href={profileData.linkedin} target="_blank" rel="noopener noreferrer"
                   className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#C9A646] transition-colors duration-300">
                  <Linkedin size={16} />
                </a>
                <a href={profileData.whatsapp} target="_blank" rel="noopener noreferrer"
                   className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-green-500 transition-colors duration-300">
                  <MessageCircle size={16} />
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 mt-12 pt-8 text-center">
            <p className="text-gray-500 font-body text-sm">
              @ {new Date().getFullYear()} vslabourlaw.com. All rights reserved.
            </p>
            <p className="text-gray-400 font-body text-xs mt-2">
              A Web Solution by <a href="https://shansoai.com" target="_blank" rel="noopener noreferrer" className="text-[#C9A646] hover:text-[#B8953B] transition-colors">shansoai.com</a>
            </p>
          </div>
        </div>
      </footer>

      {/* WhatsApp FAB */}
      <a
        href={profileData.whatsapp}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle size={24} className="text-white fill-white" />
      </a>

      {/* Scroll to top */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 left-6 z-50 w-10 h-10 bg-[#0B1F3A] hover:bg-[#C9A646] rounded-full flex items-center justify-center shadow-lg transition-colors duration-300"
          aria-label="Scroll to top"
        >
          <ArrowUp size={18} className="text-white" />
        </button>
      )}

      {/* AI Chatbot */}
      <Chatbot />
    </div>
  );
};

export default Layout;
